import { useEffect, useRef } from "react";
import type { ClaimResult } from "@b2bportal/core-media";
import { IconNameEnum } from "@b2bportal/core-themes";
import type { LodgingTravelWalletOffer } from "@b2bportal/lodging-api";
import type {
  Ad,
  OfferClaimedAssets,
  OfferDiscount,
  OfferTemplate,
} from "@b2bportal/media-ads-api";
import { OfferDiscountEnum } from "@b2bportal/media-ads-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum, Icon, Pill, PillTone } from "@tenant/ui";
import dayjs from "dayjs";
import { useFormatOfferDiscount } from "../hooks/useFormatOfferDiscount";
import { OfferDetailsDialog } from "@/modules/wallet/OfferDetailsDialog";
import {
  IsOfferClaimed,
  OfferRawEventName,
  OfferScreen,
  OfferType,
  useEmitRbcRawEvent,
} from "@/tracking/offers";

/** What the dialog shows; built from a fresh claim or an already-claimed offer. */
export interface HtsMediaOfferDialogContent {
  offerTemplate: OfferTemplate;
  assets: OfferClaimedAssets;
  /** Headline; defaults to the claimed asset title. */
  title?: string;
  /** Preferred hero image (the ad creative), falling back to the assets. */
  heroImageUrl?: string;
}

export const claimDialogContent = (
  ad: Ad,
  claimResult: ClaimResult
): HtsMediaOfferDialogContent | undefined =>
  ad.offerTemplate == null
    ? undefined
    : {
        offerTemplate: ad.offerTemplate,
        assets: claimResult.assets,
        title: ad.creative.titleText,
        heroImageUrl: ad.creative.imageUrl,
      };

export const claimedOfferDialogContent = (
  offer: OfferTemplate
): HtsMediaOfferDialogContent => ({
  offerTemplate: offer,
  assets: offer.assets,
  title: offer.assets.bannerText ?? offer.assets.title,
});

/**
 * Dialog content for a loaded offer as the lodging responses carry it, so it
 * opens the same dialog as a claimable ad. The BFF puts the campaign image in
 * `imageFolderUrl` and the byline and terms in `descriptions`; the offer's own
 * discount and expiry fill the badge and valid-until.
 */
export const lodgingOfferDialogContent = (
  offer: LodgingTravelWalletOffer,
  offerName: string
): HtsMediaOfferDialogContent => {
  const discount: OfferDiscount | undefined =
    offer.percentage != null
      ? {
          OfferDiscount: OfferDiscountEnum.Percentage,
          // Scranton percentages are fractions (0.3); the ads API uses whole numbers (30).
          discountPercent: Math.round(offer.percentage * 100),
          maxDiscountAmount: String(offer.maxDiscountAmount?.amount ?? 0),
          currency: offer.maxDiscountAmount?.currency ?? offer.amount.currency,
        }
      : {
          OfferDiscount: OfferDiscountEnum.Specific,
          amount: String(offer.amount.amount),
          currency: offer.amount.currency,
        };
  const assets: OfferClaimedAssets = {
    title: offer.title,
    walletDisplayTitle: offer.title,
    terms: offer.descriptions.filter((line) => line.trim() !== ""),
    imageUrl: offer.imageFolderUrl || undefined,
  };
  return {
    offerTemplate: {
      offerTemplateId: offer.id,
      name: offerName,
      campaignId: "",
      assets,
      expiry: offer.expiresOn,
      discount,
    },
    assets,
    title: offer.title,
  };
};

export interface HtsMediaClaimDialogProps {
  open: boolean;
  content?: HtsMediaOfferDialogContent;
  ctaLabel: string;
  /** Primary button; tracked as TappedOfferModalCta. Owner decides whether to close. */
  onCtaClick: () => void;
  /** Dismissed via X / backdrop. */
  onClose: () => void;
  /** Surface the dialog opened from; segments the modal events. */
  screen?: OfferScreen;
}

const isSafeImageUrl = (url?: string) => url?.startsWith("https://") === true;

/** Offer details for a loaded HTS Media offer (post-claim confirmation or re-opened from the banner). */
export const HtsMediaClaimDialog = ({
  open,
  content,
  ctaLabel,
  onCtaClick,
  onClose,
  screen = OfferScreen.hotel_list,
}: HtsMediaClaimDialogProps) => {
  const { t, formatDateTime } = useI18nContext();
  const formatOfferDiscount = useFormatOfferDiscount();

  const emitViewedModal = useEmitRbcRawEvent(
    OfferRawEventName.ViewedOfferDetailsModal
  );
  const emitTappedCta = useEmitRbcRawEvent(
    OfferRawEventName.TappedOfferModalCta
  );

  const offerTemplate = content?.offerTemplate;
  const trackingProperties = {
    offer_type: OfferType.hts_media,
    offer_id: offerTemplate?.offerTemplateId ?? "",
    offer_name: offerTemplate?.name ?? "",
    screen,
  };

  const hasEmittedViewRef = useRef(false);
  useEffect(() => {
    if (open && !hasEmittedViewRef.current) {
      hasEmittedViewRef.current = true;
      emitViewedModal({
        properties: {
          ...trackingProperties,
          offer_valid_until: offerTemplate?.expiry,
          is_offer_claimed: IsOfferClaimed.true,
          is_claimable: true,
        },
      });
    } else if (!open) {
      hasEmittedViewRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (content == null || offerTemplate == null) return null;

  const { assets } = content;
  const title = content.title ?? assets.title;
  const heroImage = [
    content.heroImageUrl,
    assets.wideImageUrl,
    assets.imageUrl,
  ].find(isSafeImageUrl);
  const badge = formatOfferDiscount(offerTemplate.discount);
  const expiry =
    offerTemplate.expiry != null
      ? formatDateTime(
          dayjs(offerTemplate.expiry).toDate(),
          DateTimeFormatStyle.Custom({
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        )
      : undefined;
  const details = assets.terms;

  const handleCta = () => {
    emitTappedCta({
      properties: { ...trackingProperties, is_claimable: true },
    });
    onCtaClick();
  };

  return (
    <OfferDetailsDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      title={title}
      heroImageSrc={heroImage}
      details={(cn) => (
        <>
          <div className={cn("section", ["heading"])}>
            <h2 className={cn("title")}>{title}</h2>
            {badge != null && (
              <Pill
                className={cn("badge", ["sunburst"])}
                size="custom"
                tone={PillTone.custom}
              >
                {badge}
              </Pill>
            )}
          </div>
          {expiry != null && (
            <div className={cn("section")}>
              <h3 className={cn("section-title")}>
                {t("htsMedia.dialog.validUntil")}
              </h3>
              <div className={cn("valid-until")}>
                <Icon iconName={IconNameEnum.timer} aria-hidden />
                <span>{expiry}</span>
              </div>
            </div>
          )}
          {details.length > 0 && (
            <div className={cn("section")}>
              <h3 className={cn("section-title")}>
                {t("htsMedia.dialog.offerDetails.title")}
              </h3>
              <ul className={cn("details-list")}>
                {details.map((line) => (
                  <li key={line} className={cn("details-item")}>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      howItWorks={(cn) => (
        <>
          <h3 className={cn("how-it-works-title")}>
            {t("htsMedia.dialog.howItWorks.title")}
          </h3>
          <p className={cn("how-it-works-step")}>
            {t("htsMedia.dialog.howItWorks.readyToUse")}
          </p>
          <p className={cn("how-it-works-step")}>
            {t("htsMedia.dialog.howItWorks.bookEligibleHotel")}
          </p>
          <p className={cn("how-it-works-step")}>
            {t("htsMedia.dialog.howItWorks.autoApplied")}
          </p>
        </>
      )}
      ctaRow={(cn) => (
        <>
          <span className={cn("loaded")}>
            {t("offerBanner.loaded")}
            <Icon iconName={IconNameEnum.checkFilled} aria-hidden />
          </span>
          <Button
            className={cn("cta")}
            buttonType={ButtonTypeEnum.Primary}
            onClick={handleCta}
          >
            {ctaLabel}
          </Button>
        </>
      )}
      // TODO(HAM-2695): terms copy is not defined yet; pass `terms` once it is.
    />
  );
};
